import React, { useEffect, useState } from 'react';
import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import {
  ChartColorLegend,
  ChartLegendBogItem,
  ChartLegendGridItem,
  ChartNoDataGridItem,
  DashboardChartWrapper,
} from 'pages/DashboardPage/PieCharts/styles';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { colors } from 'theme/colors';
import Button from 'components/atoms/Button';
import { EButtonSizes, EButtonVariants } from 'constants/Buttons';
import { useNavigate } from 'react-router-dom';
import { ERouteLinks } from 'models/route';
import { getLegalEntities } from 'requests/dashboard';
import { useTranslations } from 'hooks/useTranslations';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { EDashboardSortType } from '../../../models/dashboard';

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

interface ILegalEntitiesChart {
  sortType: EDashboardSortType;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
}

const LegalEntitiesChart = ({ sortType, setLoader }: ILegalEntitiesChart) => {
  const navigate = useNavigate();
  const [legalEntitiesData, setLegalEntitiesData] = useState<[number, number]>();
  const [totalValue, setTotalValue] = useState(1);
  const { t } = useTranslations();
  const { isDesktop, isMobile } = useThemeBreakpoint();
  const [noData, setNoData] = useState(false);
  const [noDataText, setNoDataText] = useState<string>('');

  const [options] = useState({
    rotation: -111,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: true,
        color: '#ffffff',
        borderRadius: 3,
        font: {
          size: 18,
          weight: 600,
          lineHeight: 1,
        },
        formatter: (value: number) => (value > 0 ? `${value}%` : ''),
      },
    },
  });

  useEffect(() => {
    (async () => {
      setLoader(true);
      const data = await getLegalEntities(sortType);
      if (data?.chartValues) {
        const entities: [number, number] = [
          data.chartValues?.[0]?.value,
          data.chartValues?.[1]?.value,
        ];
        if (entities.every((item) => item === 0)) {
          setNoData(true);
        } else {
          setNoData(false);
        }
        setLegalEntitiesData(entities);
        setTotalValue(entities.reduce((total, current) => total + current, 0));
      } else {
        setNoData(true);
      }
      setLoader(false);
    })();
  }, [sortType]);

  useEffect(() => {
    switch (sortType) {
      case EDashboardSortType.Day: {
        setNoDataText('dashboard.chart.legal.entities.noDataToday');
        break;
      }
      case EDashboardSortType.Week: {
        setNoDataText('dashboard.chart.legal.entities.noData7days');
        break;
      }
      case EDashboardSortType.Month: {
        setNoDataText('dashboard.chart.legal.entities.noData30days');
        break;
      }
      case EDashboardSortType.Year: {
        setNoDataText('dashboard.chart.legal.entities.noDataYear');
        break;
      }
      case EDashboardSortType.All: {
        setNoDataText('dashboard.chart.legal.entities.noDataAll');
        break;
      }
      default: {
        setNoDataText('dashboard.chart.legal.entities.noDataAll');
        break;
      }
    }
  }, [sortType]);

  const data =
    legalEntitiesData?.length && totalValue
      ? {
          datasets: [
            {
              data: [
                Number(((legalEntitiesData[0] / totalValue) * 100).toFixed(1)),
                Number(((legalEntitiesData[1] / totalValue) * 100).toFixed(1)),
              ],
              height: '400px',
              backgroundColor: ['rgba(0, 50, 85, 1)', 'rgba(19, 116, 172, 1)'],
              borderColor: ['rgba(0, 50, 85, 1)', 'rgba(19, 116, 172, 1)'],
              borderWidth: 1,
            },
          ],
        }
      : undefined;

  const plugins = [
    {
      id: 'custom_canvas_background_color',
      resize: (chart: { height: any }) => {
        const { height } = chart;
        const fontSize = Number((height / 9).toFixed(0));
        options.plugins.datalabels.font = {
          size: fontSize < 13 ? 13 : fontSize,
          weight: 600,
          lineHeight: 1,
        };
      },
    },
  ];

  const chartRender = (
    <Grid
      container
      justifyContent="center"
      sx={{
        height: isDesktop ? 'fit-content' : 'auto',
        flexDirection: !isMobile ? 'row' : 'column-reverse',
      }}
    >
      <Grid
        container
        item
        sm={7}
        xs={12}
        direction="row"
        alignItems="center"
        alignContent="center"
        sx={{
          padding: '10px 10px',
          ...(isMobile && { marginTop: '20px' }),
          ...(isDesktop && { minWidth: '190px' }),
        }}
      >
        <ChartLegendGridItem
          container
          item
          xs={12}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.primary900} />
            <Typography variant="body2">
              {t('dashboard.chart.legal.entities.organizations')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalEntitiesData?.[0]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          container
          item
          xs={12}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center' }) }}
        >
          <ChartLegendBogItem>
            <ChartColorLegend color={colors.primary400} />
            <Typography variant="body2">
              {t('dashboard.chart.legal.entities.individual')}
              <Typography variant="subtitle2" sx={{ display: 'inline', paddingLeft: 1.5 }}>
                {legalEntitiesData?.[1]}
              </Typography>
            </Typography>
          </ChartLegendBogItem>
        </ChartLegendGridItem>

        <ChartLegendGridItem
          item
          xs={12}
          sx={{ ...(!isMobile ? {} : { justifyContent: 'center', textAlign: 'center' }) }}
        >
          <Typography variant="body2">
            {t('dashboard.chart.legal.entities.total').toUpperCase()}
            <Typography
              variant="subtitle2"
              component="p"
              sx={{ display: 'inline', paddingLeft: 1.5 }}
            >
              {totalValue}
            </Typography>
          </Typography>
        </ChartLegendGridItem>
      </Grid>
      <Grid
        item
        sm={5}
        xs={12}
        sx={{
          ...(!isMobile
            ? { display: 'block', alignSelf: 'center' }
            : { display: 'flex', justifyContent: 'center' }),
        }}
      >
        <Box sx={{ ...(isMobile ? { width: '40vw' } : { width: '100%', height: '100%' }) }}>
          {data && (
            <Pie
              data={data}
              options={options}
              plugins={plugins}
              style={{
                ...(isDesktop
                  ? { minWidth: '10vw', minHeight: '10vw' }
                  : { minWidth: '20vw', minHeight: '20vw' }),
                ...(isDesktop ? { maxHeight: '300px', maxWidth: '300px' } : {}),
              }}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  );

  const noDataRender = (
    <ChartNoDataGridItem container item xs={12}>
      <Typography variant="body2">{t(noDataText)}</Typography>
    </ChartNoDataGridItem>
  );

  return (
    <DashboardChartWrapper>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', marginBottom: 2 }}>
        {t('dashboard.chart.legal.entities.label')}
      </Typography>

      {noData ? noDataRender : chartRender}
      <Box
        sx={{
          ...(!isMobile ? { padding: '20px 10px 0px 10px' } : { padding: '5px 10px 0 10px' }),
        }}
      >
        <Button
          variant={EButtonVariants.outlined}
          fullWidth={true}
          label={t('dashboard.chart.legal.entities.seeLegalEntities')}
          onClick={() => navigate(ERouteLinks.LegalEntities)}
          size={EButtonSizes.small}
        />
      </Box>
    </DashboardChartWrapper>
  );
};

export default LegalEntitiesChart;
